/**
 * QA-302 — 주문 상세 응답의 상품 추가입력(productCollectionInputs)을 주문상품 품목에 붙인다.
 *
 * 고도몰은 텍스트 옵션·이벤트 수집 항목(예: Shipping Fee Refund, 이름, 연락처, 생년월일)을 주문상품 줄마다 받아
 * 줄마다 보여 줬다(es_orderGoods.optionTextInfo, OrderAdmin.php 1687~1694 "[항목 : 값]"). 이관 주문의 이 값은
 * order-service 가 품목 단위로 갖고 있어(V75 order_item_id) 관리자 상세 응답에 실려 온다 — catalog 조회
 * (신규 주문 전용)에는 없다.
 *
 * 붙이는 규칙은 BE CollectionInputPlacement 와 같다:
 *  - orderItemId 가 있으면(이관 주문) 그 품목에.
 *  - 없으면(신규 주문) 같은 상품의 품목이 하나일 때만 그 품목에. 여럿이면 어느 품목인지 정할 수 없어 붙이지 않는다.
 */

export interface OrderItemInputRow {
  label: string;
  value: string;
  orderItemId?: string | null;
  productId?: string | null;
  productCode?: string | null;
}

export interface OrderItemInput {
  label: string;
  value: string;
}

const isRow = (v: unknown): v is OrderItemInputRow =>
  typeof v === 'object' && v !== null && typeof (v as { label?: unknown }).label === 'string';

/** 응답의 productCollectionInputs 를 안전하게 읽는다(generated 타입 미반영). */
export const readOrderInputRows = (order: unknown): OrderItemInputRow[] => {
  const raw = (order as { productCollectionInputs?: unknown } | null | undefined)?.productCollectionInputs;
  return Array.isArray(raw) ? raw.filter(isRow) : [];
};

/** 품목 id → 그 품목의 입력(응답 순서 = 품목 순서 → 표시 순서). */
export const placeOrderItemInputs = (
  items: ReadonlyArray<{ orderItemId: string; productId: string }>,
  rows: ReadonlyArray<OrderItemInputRow>,
): Map<string, OrderItemInput[]> => {
  const itemIds = new Set(items.map((i) => i.orderItemId));
  const itemsByProduct = new Map<string, string[]>();
  items.forEach((i) => itemsByProduct.set(i.productId, [...(itemsByProduct.get(i.productId) ?? []), i.orderItemId]));

  const out = new Map<string, OrderItemInput[]>();
  const push = (id: string, r: OrderItemInputRow) =>
    out.set(id, [...(out.get(id) ?? []), { label: r.label, value: r.value ?? '' }]);

  rows.forEach((r) => {
    if (r.orderItemId) {
      if (itemIds.has(r.orderItemId)) push(r.orderItemId, r);
      return;
    }
    const candidates = r.productId ? (itemsByProduct.get(r.productId) ?? []) : [];
    if (candidates.length === 1) push(candidates[0]!, r);
  });
  return out;
};

/**
 * 이관 주문 품목 — 입력이 orderItemId 로 붙은 품목(고도몰 이관만 품목 id 를 갖는다). 이 품목은 order-service 값이
 * 표시 기준이다(BE 회신 10/16: 텍스트 옵션 원문 복원 + 이벤트 옵션 이름·연락처·이메일·생년월일 … 추가). catalog 조회에
 * 남아 있는 이관 당시 값(Shipping Fee Refund 만, ******** 마스킹)은 쓰지 않는다.
 */
export const migratedInputItemIds = (
  items: ReadonlyArray<{ orderItemId: string }>,
  rows: ReadonlyArray<OrderItemInputRow>,
): Set<string> => {
  const itemIds = new Set(items.map((i) => i.orderItemId));
  return new Set(rows.map((r) => r.orderItemId).filter((id): id is string => !!id && itemIds.has(id)));
};
